import React from 'react';
import { AlertTriangleIcon } from './Icons';
import { useModalAccessibility } from '../hooks/useModalAccessibility';
import './SyncConfirmModal.css';

interface SyncConfirmModalProps {
  isOpen: boolean;
  localDataCount: {
    investments: number;
    portfolioSettings: boolean;
    assetSettings: boolean;
    fixedIncomes: number;
    fixedExpenses: number;
    monthlyBalances: number;
  };
  onConfirm: () => void;
  onCancel: () => void;
}

const SyncConfirmModal: React.FC<SyncConfirmModalProps> = ({
  isOpen,
  localDataCount,
  onConfirm,
  onCancel
}) => {
  const { modalRef } = useModalAccessibility(isOpen, onCancel);
  if (!isOpen) return null;

  const hasLocalData = 
    localDataCount.investments > 0 ||
    localDataCount.portfolioSettings ||
    localDataCount.assetSettings ||
    localDataCount.fixedIncomes > 0 ||
    localDataCount.fixedExpenses > 0 ||
    localDataCount.monthlyBalances > 0;

  if (!hasLocalData) {
    // 로컬 데이터가 없으면 모달을 표시하지 않고 바로 진행
    return null;
  }

  return (
    <div className="sync-confirm-modal-overlay" onClick={onCancel}>
      <div
        ref={modalRef}
        className="sync-confirm-modal modal-animated"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sync-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="sync-confirm-title">로컬 데이터 동기화</h2>
        <p className="sync-confirm-description">
          로그인하지 않은 상태에서 작성한 데이터가 있습니다.
          <br />
          이 데이터를 계정에 동기화하시겠습니까?
        </p>
        
        <div className="sync-confirm-data-list">
          {localDataCount.investments > 0 && (
            <div className="sync-confirm-data-item">
              <span className="data-label">투자 기록:</span>
              <span className="data-count">{localDataCount.investments}건</span>
            </div>
          )}
          {localDataCount.portfolioSettings && (
            <div className="sync-confirm-data-item">
              <span className="data-label">포트폴리오 설정:</span>
              <span className="data-count">있음</span>
            </div>
          )}
          {localDataCount.assetSettings && (
            <div className="sync-confirm-data-item">
              <span className="data-label">자산 설정:</span>
              <span className="data-count">있음</span>
            </div>
          )}
          {localDataCount.fixedIncomes > 0 && (
            <div className="sync-confirm-data-item">
              <span className="data-label">고정 수입:</span>
              <span className="data-count">{localDataCount.fixedIncomes}건</span>
            </div>
          )}
          {localDataCount.fixedExpenses > 0 && (
            <div className="sync-confirm-data-item">
              <span className="data-label">고정 지출:</span>
              <span className="data-count">{localDataCount.fixedExpenses}건</span>
            </div>
          )}
          {localDataCount.monthlyBalances > 0 && (
            <div className="sync-confirm-data-item">
              <span className="data-label">월별 잔액:</span>
              <span className="data-count">{localDataCount.monthlyBalances}건</span>
            </div>
          )}
        </div>

        <div className="sync-confirm-warning">
          <p><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><AlertTriangleIcon size={16} color="currentColor" /></span> 동기화하지 않으면:</p>
          <ul>
            <li>로컬 데이터는 그대로 유지됩니다</li>
            <li>로그인한 계정의 데이터만 표시됩니다</li>
            <li>로그아웃 시 로컬 데이터가 다시 표시됩니다</li>
          </ul>
        </div>

        <div className="sync-confirm-buttons">
          <button 
            className="sync-confirm-button sync-confirm-button-primary"
            onClick={onConfirm}
          >
            동기화하기
          </button>
          <button 
            className="sync-confirm-button sync-confirm-button-secondary"
            onClick={onCancel}
          >
            동기화하지 않기
          </button>
        </div>
      </div>
    </div>
  );
};

export default SyncConfirmModal;

